import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, Lock, Unlock, ExternalLink } from 'lucide-react';

const endpoints = [
  {
    group: 'Authentication',
    description: 'Zwei Auth-Methoden: JWT Bearer Token (für UI) oder statischer API Key (für externe Systeme). Header: "Authorization: Bearer {token}" oder "X-API-Key: delist_..."',
    items: [
      {
        method: 'POST',
        path: '/api/auth/login',
        description: 'Login with email/password, returns JWT token',
        auth: false,
        body: '{"email": "admin@agent.provider.tools", "password": "..."}',
        response: '{"access_token": "eyJ...", "token_type": "bearer", "expires_at": "...", "user": {...}}',
      },
      {
        method: 'GET',
        path: '/api/auth/me',
        description: 'Get current user profile',
        auth: true,
        response: '{"id": "...", "email": "...", "name": "...", "role": "admin"}',
      },
    ],
  },
  {
    group: 'API Keys',
    description: 'Statische API Keys als Alternative zum JWT Login. Ideal für Monitoring-Systeme und Automatisierung.',
    items: [
      {
        method: 'GET',
        path: '/api/api-keys',
        description: 'List all API keys for your organization',
        auth: true,
        response: '{"keys": [{"id": "...", "name": "Monitoring", "key_prefix": "delist_a3b1", "is_active": true, "last_used_at": "..."}]}',
      },
      {
        method: 'POST',
        path: '/api/api-keys',
        description: 'Create a new API key. The raw key is shown ONLY ONCE in the response!',
        auth: true,
        body: '{"name": "Monitoring System"}',
        response: '{"id": "...", "name": "...", "key": "delist_abc123...", "warning": "Save this key now!"}',
      },
      {
        method: 'DELETE',
        path: '/api/api-keys/{key_id}',
        description: 'Revoke an API key permanently',
        auth: true,
        response: '{"status": "revoked"}',
      },
    ],
  },
  {
    group: 'Delisting Jobs',
    items: [
      {
        method: 'POST',
        path: '/api/delist',
        description: 'Create a new delisting job. Starts the autonomous agent.',
        auth: true,
        body: JSON.stringify({
          ip: '203.0.113.42',
          blacklist: 'cbl.abuseat.org',
          blacklist_name: 'CBL',
        }, null, 2),
        response: '{"job_id": "uuid", "status": "researching", "agent_name": "Hans Müller", "agent_position": "Network Administrator", "created_at": "..."}',
      },
      {
        method: 'GET',
        path: '/api/delist/{job_id}',
        description: 'Get detailed job status with timeline, checklist, and email counts',
        auth: false,
        response: '{"job_id": "...", "status": "...", "checklist": [...], "timeline": [...]}',
      },
      {
        method: 'GET',
        path: '/api/delist',
        description: 'List all jobs with pagination',
        auth: false,
        response: '{"jobs": [...], "total": 42, "limit": 20, "offset": 0}',
      },
      {
        method: 'DELETE',
        path: '/api/delist/{job_id}',
        description: 'Cancel a running job',
        auth: true,
        response: '{"job_id": "...", "status": "cancelled"}',
      },
      {
        method: 'PATCH',
        path: '/api/delist/{job_id}/context',
        description: 'Add additional info to a running job (hostname, blocked email, notes). Merges into existing context. Agent picks this up automatically.',
        auth: true,
        body: JSON.stringify({
          context: {
            server_hostname: 'mail.example.com',
            blocked_email: 'bounced@example.com',
            additional_notes: 'IP was used for transactional mail only',
          },
        }, null, 2),
        response: '{"status": "updated", "context": {...}, "keys_added": ["server_hostname", ...]}',
      },
      {
        method: 'POST',
        path: '/api/delist/{job_id}/messages',
        description: 'Send an instruction to the running agent (e.g. "Send interim report to admin@example.com"). Agent interprets via LLM and can: send emails, add tasks to checklist, adjust strategy.',
        auth: true,
        body: '{"content": "Schick einen Zwischenbericht an admin@example.com"}',
        response: '{"status": "sent", "message": "Instruction queued for agent"}',
      },
      {
        method: 'GET',
        path: '/api/delist/{job_id}/messages',
        description: 'Get all messages (user instructions + agent responses) for a job',
        auth: true,
        response: '{"messages": [{"role": "user", "content": "...", "read": true, "created_at": "..."}]}',
      },
    ],
  },
  {
    group: 'Webhook',
    items: [
      {
        method: 'POST',
        path: '/api/webhook/email?token=SECRET',
        description: 'Receive inbound emails from Postal. Smart matching: 0) in_reply_to header → match sent email token (most reliable), 1) By job-ID in recipient, 2) By sender (reply), 3) By IP in body. Unmatched emails are stored for admin review.',
        auth: false,
        body: '{"from": "support@bl.example.com", "to": "delist-{job-id}@agent.provider.tools", "subject": "...", "body_text": "..."}',
        response: '{"status": "processed|unmatched", "job_id": "...", "match_method": "in_reply_to|recipient_address|sender_match|ip_in_body", "outcome": "success|denied|needs_action"}',
      },
    ],
  },
  {
    group: 'Email Inbox (Admin)',
    items: [
      {
        method: 'GET',
        path: '/api/admin/emails/unmatched',
        description: 'List unmatched inbound emails. Supports pagination via limit & offset.',
        auth: true,
        admin: true,
        response: '{"total": 5, "emails": [{"id": "...", "from_addr": "...", "subject": "...", "body_text": "...", "analysis": {"ips_found": ["1.2.3.4"]}, "created_at": "..."}]}',
      },
      {
        method: 'POST',
        path: '/api/admin/emails/{email_id}/assign',
        description: 'Manually assign an unmatched email to a job. Triggers LLM analysis and may auto-complete the job.',
        auth: true,
        admin: true,
        body: '{"job_id": "uuid-of-the-job"}',
        response: '{"status": "assigned", "analysis_outcome": "success|denied|unknown"}',
      },
      {
        method: 'DELETE',
        path: '/api/admin/emails/{email_id}',
        description: 'Dismiss/delete an unmatched email',
        auth: true,
        admin: true,
        response: '{"status": "deleted"}',
      },
    ],
  },
  {
    group: 'Email Blacklist',
    items: [
      {
        method: 'GET',
        path: '/api/admin/email-blacklist',
        description: 'List globally blacklisted email addresses (active entries within 30-day TTL). Agents skip these addresses to save resources.',
        auth: true,
        admin: true,
        response: '[{"id": "uuid", "email_address": "bounce@dead.com", "reason": "hard_fail", "job_id": "uuid|null", "created_at": "ISO"}]',
      },
      {
        method: 'DELETE',
        path: '/api/admin/email-blacklist/{entry_id}',
        description: 'Remove an email from the global blacklist (allows agents to try it again)',
        auth: true,
        admin: true,
        response: '{"status": "removed", "id": "uuid"}',
      },
    ],
  },
  {
    group: 'Knowledge Base',
    items: [
      {
        method: 'GET',
        path: '/api/knowledge',
        description: 'KB overview statistics',
        auth: false,
        response: '{"total_blacklists": 8, "total_methods": 14, "total_learnings": 42}',
      },
      {
        method: 'GET',
        path: '/api/knowledge/blacklists',
        description: 'List all known blacklists with their methods',
        auth: false,
      },
      {
        method: 'GET',
        path: '/api/knowledge/learnings',
        description: 'List agent learnings (audit log)',
        auth: false,
      },
    ],
  },
  {
    group: 'User Management',
    items: [
      {
        method: 'GET',
        path: '/api/users',
        description: 'List all users (admin only)',
        auth: true,
        admin: true,
      },
      {
        method: 'POST',
        path: '/api/users',
        description: 'Create a new user (admin only)',
        auth: true,
        admin: true,
        body: '{"email": "...", "password": "...", "name": "...", "role": "operator"}',
      },
      {
        method: 'PUT',
        path: '/api/users/{user_id}',
        description: 'Update user (admin only)',
        auth: true,
        admin: true,
      },
      {
        method: 'DELETE',
        path: '/api/users/{user_id}',
        description: 'Delete user (admin only)',
        auth: true,
        admin: true,
      },
    ],
  },
  {
    group: 'System',
    items: [
      {
        method: 'GET',
        path: '/api/health',
        description: 'Health check',
        auth: false,
        response: '{"status": "ok", "service": "delisting-agent"}',
      },
      {
        method: 'GET',
        path: '/api/stats',
        description: 'Dashboard statistics (job counts, success rate, KB size)',
        auth: false,
      },
    ],
  },
];

const methodColors = {
  GET: 'bg-emerald-500/20 text-emerald-400',
  POST: 'bg-blue-500/20 text-blue-400',
  PUT: 'bg-amber-500/20 text-amber-400',
  PATCH: 'bg-violet-500/20 text-violet-400',
  DELETE: 'bg-rose-500/20 text-rose-400',
};

export default function ApiDocsPage() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-6" data-testid="api-docs-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">API Documentation</h2>
          <p className="text-muted-foreground">REST API reference for the KI Delisting Agent</p>
        </div>
        {backendUrl && (
          <a
            href={`${backendUrl}/docs`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            OpenAPI (Swagger)
          </a>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Authentication</CardTitle>
          <CardDescription>
            Zwei Auth-Methoden werden unterstützt:
            <br />1. <strong>JWT Bearer Token</strong> (für UI): <code className="text-xs bg-secondary px-1 py-0.5 rounded">Authorization: Bearer {'<token>'}</code>
            <br />2. <strong>API Key</strong> (für externe Systeme): <code className="text-xs bg-secondary px-1 py-0.5 rounded">X-API-Key: delist_...</code>
          </CardDescription>
        </CardHeader>
      </Card>

      {endpoints.map((group) => (
        <Card key={group.group}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{group.group}</CardTitle>
            {group.description && (
              <CardDescription className="text-xs">{group.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {group.items.map((ep, i) => (
              <div key={i} className="border border-border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={`${methodColors[ep.method]} border-0 font-mono text-xs px-2`}>
                    {ep.method}
                  </Badge>
                  <code className="text-sm font-mono">{ep.path}</code>
                  {ep.auth ? (
                    <span className="flex items-center gap-1 text-xs text-amber-400">
                      <Lock className="w-3 h-3" />
                      {ep.admin ? 'Admin' : 'Auth'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Unlock className="w-3 h-3" />
                      Public
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{ep.description}</p>
                {ep.body && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Request Body:</p>
                    <pre className="text-xs bg-secondary/50 rounded p-2 overflow-x-auto font-mono">{ep.body}</pre>
                  </div>
                )}
                {ep.response && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Response:</p>
                    <pre className="text-xs bg-secondary/50 rounded p-2 overflow-x-auto font-mono">{ep.response}</pre>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
